import React from 'react';
import { BadgeCheck, Heart } from 'lucide-react';
import { MemberAvatar } from '@/components/design';
import { cityFlag as flag } from '@/utils/cityFlag';
import { frDate } from '@/utils/currencyUtils';
import { FLEX_LABELS, TRANSPORT_LABELS, URGENCY_LABELS } from '@/utils/listingParsers';
import { daysClass } from '@/utils/daysClass';
import '@/styles/annonce-card.css';

/** Libellés partagés écrits avec un emoji en tête (« ✈️ Avion ») : affichés sans lui (icônes Lucide seulement). */
const sansEmoji = (label) => String(label || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
const kg = (n) => `${Number(n).toLocaleString('fr-FR')} kg`;

/**
 * Carte d'une annonce (direction C « Lien », 2026-10-29) : page des annonces et favoris de « Mes colis » /
 * « Mes trajets ». Auteur, route en grand, date · mode · poids, prix dans sa devise d'origine (jamais converti).
 *
 *   <ModernAnnonceCard a={a} index={i} isFav={...} onFav={...} onClick={...} proposalsCount={...}/>
 *
 * `a` est l'objet normalisé de la page : { id, type: 'colis' | 'trajet', from, to, days, daysNum, price, unit,
 * weight, hot, user: { name, score, trips, verified, pro, proPending, subscribed }, raw }.
 * `preview` : aperçu du récapitulatif de publication (ni lien ni favori). Urgence choisie par l'expéditeur
 * (`raw.urgency`) et souplesse de la date (`raw.flex_departure`) : affichées depuis le 2026-11-03.
 */
export default function ModernAnnonceCard({ a, isFav, onFav, onClick, index = 0, proposalsCount = 0, preview = false }) {
  const isTrip = a.type === 'trajet';
  const author = a.raw?.author || a.raw?.owner || {};
  const [first = '', last = ''] = (a.user?.name || '').split(' ');
  const rated = a.user?.score && a.user.score !== 'Nouveau';
  const modes = isTrip ? (a.raw?.transport_modes || []).map((m) => sansEmoji(TRANSPORT_LABELS[m] || m).toLowerCase()) : [];
  const date = a.raw?.departure_date ? frDate(String(a.raw.departure_date).slice(0, 10)) : null;
  const priceLabel = isTrip && a.raw?.pricing_mode === 'libre'
    ? 'Prix à proposer'
    : !a.price ? (isTrip ? 'Prix à proposer' : 'Budget libre')
      : `${typeof a.price === 'number' ? a.price.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : a.price} ${a.unit}`;
  const flex = a.raw?.flex_departure && a.raw.flex_departure !== 'fixe' ? FLEX_LABELS[a.raw.flex_departure] : null;
  // Urgence déclarée par l'expéditeur ; « Urgent · J-3 » (date proche) reste prioritaire
  const declared = !isTrip && !a.hot && ['urgent', 'priority'].includes(a.raw?.urgency) ? URGENCY_LABELS[a.raw.urgency] : null;
  const meta = [
    date && (isTrip ? `départ le ${date}` : `avant le ${date}`) + (date && flex ? `, ${flex}` : ''),
    ...modes,
    a.weight ? (isTrip ? `${kg(a.weight)} de libre` : kg(a.weight)) : null,
  ].filter(Boolean);

  return (
    <article
      className="ac-card"
      style={{ animationDelay: `${Math.min(index, 9) * 0.04}s` }}
      onClick={onClick}
      data-testid={`mcard-${a.type}-${a.id}`}
    >
      <div className="ac-head">
        <MemberAvatar firstName={author.first_name || first} lastName={author.last_name_initial || author.last_name || last} size={48} />
        <div className="ac-author">
          <div className="ac-author-name">{a.user?.name}</div>
          <div className="ac-author-sub">
            {a.user?.pro && !a.user?.proPending && <>Entreprise · </>}
            {rated ? <>★ {a.user.score} · {a.user.trips} avis</> : 'Nouveau membre'}
          </div>
        </div>
        {a.user?.verified && (
          <span className="ac-verified"><BadgeCheck size={13} aria-hidden="true" /> vérifié</span>
        )}
        {onFav && !preview && (
          <button
            type="button"
            className={`ac-fav${isFav ? ' is-on' : ''}`}
            onClick={(e) => { e.stopPropagation(); onFav(); }}
            aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            aria-pressed={!!isFav}
            data-testid={`mcard-fav-${a.id}`}
          >
            <Heart size={16} fill={isFav ? 'currentColor' : 'none'} aria-hidden="true" />
          </button>
        )}
      </div>

      {(a.hot || declared || a.user?.subscribed || proposalsCount > 0) && (
        <div className="ac-badges">
          {a.hot && <span className="ac-badge ac-badge--urgent">Urgent · {a.days}</span>}
          {declared && <span className={`ac-badge${a.raw.urgency === 'urgent' ? ' ac-badge--urgent' : ''}`} data-testid="mcard-urgency">{declared}</span>}
          {a.user?.subscribed && <span className="ac-badge ac-badge--pro" title="Transporteur abonné PRO">PRO</span>}
          {proposalsCount > 0 && (
            <span className="ac-badge">{proposalsCount} proposition{proposalsCount > 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      <h2 className="ac-route">
        <span>{flag(a.from)} {a.from}</span>
        <span className="ac-arrow" aria-hidden="true">→</span>
        <span>{flag(a.to)} {a.to}</span>
      </h2>
      {meta.length > 0 && (
        <p className="ac-meta">
          {meta.join(' · ')}
          {!a.hot && a.daysNum != null && <span className={`ac-days ac-days--${daysClass(a.daysNum)}`}>{a.days}</span>}
        </p>
      )}

      <div className="ac-foot">
        <span className="ac-price">{priceLabel}</span>
        {!preview && (
          <button type="button" className="ac-link" onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
            {isTrip ? 'Voir le trajet' : 'Voir le colis'}
          </button>
        )}
      </div>
    </article>
  );
}
